import type { OrderStatusResponse } from '@/utils/DatabaseOperations'
import { Badge } from '@helix-ds/react'
import { ChevronDown } from 'flowbite-react-icons/outline'
import { motion } from 'framer-motion'
import type { WorkspaceOrderGroup } from './useWorkspaceFilters'
import {
  calculateAge,
  comparatorRelationshipLabel,
  formatMrnDisplay,
  formatShortDate,
  getWorkspaceDraftActionHint,
  getWorkspaceProviderDisplay,
  getWorkspaceStatusBadgeClass,
  getWorkspaceStatusDisplay,
  isPrimaryGuardianComparatorRow,
  isTestingComparatorRow,
  workspaceOrderNumberDisplay,
} from './workspaceOrderPresentation'

export type WorkspaceOrderCardProps = {
  group: WorkspaceOrderGroup
  expanded: boolean
  onToggleComparators: (groupKey: string) => void
  onOrderClick: (order: OrderStatusResponse) => void | Promise<void>
}

const patientName = (first?: string | null, last?: string | null, fallback = 'Name N/A'): string => {
  const name = `${first ?? ''} ${last ?? ''}`.trim()
  return name || fallback
}

/**
 * Mobile/narrow-viewport card for a single workspace order group. Mirrors the table's
 * group invariant: comparators are presentation-only children of their proband.
 */
export function WorkspaceOrderCard({ group, expanded, onToggleComparators, onOrderClick }: WorkspaceOrderCardProps) {
  const { order, comparators, groupKey } = group
  const { label: statusLabel } = getWorkspaceStatusDisplay(order.status)
  const statusBadgeClass = getWorkspaceStatusBadgeClass(order.status)
  const actionHint = getWorkspaceDraftActionHint(order)
  const { main: providerMain, submittedBy: providerSubmittedBy } = getWorkspaceProviderDisplay(order)
  // Unique per-group key for regionId / expand toggle / sub-row keys so two empty-order_id groups
  // don't collide (F6). Equals order_id for the normal non-empty case, so behavior is unchanged.
  const regionId = `workspace-order-card-${groupKey}-comparators`
  const orderNumberLabel = workspaceOrderNumberDisplay(order)

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
      className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm"
    >
      <button
        type="button"
        onClick={() => void onOrderClick(order)}
        className="flex w-full flex-col gap-3 p-4 text-left focus:outline-none focus:ring-2 focus:ring-brand-600"
      >
        <div className="flex items-start justify-between gap-2">
          <span className="text-sm font-medium text-brand-600 dark:text-brand-400">{orderNumberLabel}</span>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Badge className={statusBadgeClass}>{statusLabel}</Badge>
            {actionHint && (
              <span className="text-xs font-medium text-brand-600 dark:text-brand-400">{actionHint}</span>
            )}
          </div>
        </div>
        <div>
          <div className="text-sm font-medium text-text-heading dark:text-white">
            <span className="mr-2 rounded-full bg-background-tertiary dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-text-body dark:text-gray-400">
              Proband
            </span>
            {patientName(order.patient_first_name, order.patient_last_name, 'Patient Data N/A')}
          </div>
          <div className="mt-0.5 text-xs text-text-body-subtle dark:text-gray-500">
            {order.patient_mrn
              ? formatMrnDisplay(order.patient_mrn)
              : order.external_case_key
                ? `Case ${order.external_case_key}`
                : 'No case key'}
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
          <div>
            <dt className="text-text-body-subtle dark:text-gray-500">DOB • Age • Sex</dt>
            <dd className="text-text-body dark:text-gray-400">
              {order.patient_date_of_birth ?? 'N/A'} • {calculateAge(order.patient_date_of_birth ?? undefined)} •{' '}
              {order.patient_sex ?? 'U'}
            </dd>
          </div>
          <div>
            <dt className="text-text-body-subtle dark:text-gray-500">Provider</dt>
            <dd className="text-text-body dark:text-gray-400">{providerMain}</dd>
            {providerSubmittedBy && (
              <dd className="text-text-body-subtle dark:text-gray-500">{providerSubmittedBy}</dd>
            )}
          </div>
          <div>
            <dt className="text-text-body-subtle dark:text-gray-500">Order Submitted</dt>
            <dd className="text-text-body dark:text-gray-400">{formatShortDate(order.created_at)}</dd>
          </div>
          <div>
            <dt className="text-text-body-subtle dark:text-gray-500">Last Updated</dt>
            <dd className="text-text-body dark:text-gray-400">{formatShortDate(order.updated_at)}</dd>
          </div>
        </dl>
      </button>

      {comparators.length > 0 && (
        <div className="border-t border-border-base dark:border-gray-700 px-4 pb-4 pt-3">
          <button
            type="button"
            onClick={() => onToggleComparators(groupKey)}
            className="inline-flex items-center gap-1.5 rounded-full border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-1 text-xs font-medium text-text-body dark:text-gray-400 hover:bg-background-secondary-medium dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-600"
            aria-expanded={expanded}
            aria-controls={regionId}
            aria-label={
              expanded ? `Hide comparators for order ${order.order_id}` : `Show comparators for order ${order.order_id}`
            }
          >
            <span>
              {comparators.length} {comparators.length === 1 ? 'Relation' : 'Relations'}
            </span>
            <span className={`inline-block transition-transform duration-150 ease-out ${expanded ? '' : '-rotate-90'}`}>
              <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
            </span>
          </button>

          {expanded && (
            <ul id={regionId} className="mt-3 space-y-2">
              {comparators.map((c) => {
                const isTesting = isTestingComparatorRow(c)
                const isPg = isPrimaryGuardianComparatorRow(c)
                const relLabel = comparatorRelationshipLabel(c.relationship)
                const rolePillLabel = !isTesting
                  ? isPg
                    ? 'Guardian'
                    : relLabel
                  : isPg
                    ? `Guardian · ${relLabel}`
                    : relLabel
                return (
                  <li
                    key={`${groupKey}-cmp-${c.patient_id}`}
                    className="rounded-base bg-background-secondary-medium/60 dark:bg-gray-800/60 p-3 text-xs"
                  >
                    <div className="font-medium text-text-heading dark:text-white">
                      {patientName(c.first_name, c.last_name)}
                    </div>
                    <div className="mt-1 flex items-center gap-1.5">
                      <Badge color="gray">{rolePillLabel}</Badge>
                    </div>
                    <div className="mt-1 text-text-body-subtle dark:text-gray-500">
                      {c.date_of_birth ?? 'N/A'} • {calculateAge(c.date_of_birth ?? undefined)} • {c.sex ?? 'U'}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </motion.div>
  )
}
